/**
 * TanStack Query hooks for the unknown-sender triage queue.
 * Internal-staff-only — clients see a 403 from the API.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  unknownEmailsApi,
  type UnknownEmailRow,
} from '@beacon/api-client';

import { beaconClient } from '@/lib/api/client.js';

export const unknownEmailKeys = {
  all: ['unknown-emails'] as const,
  list: (filter: 'pending' | 'all') =>
    [...unknownEmailKeys.all, 'list', filter] as const,
};

export function useUnknownEmails(filter: 'pending' | 'all' = 'pending') {
  return useQuery({
    queryKey: unknownEmailKeys.list(filter),
    queryFn: async (): Promise<UnknownEmailRow[]> => {
      const res = await unknownEmailsApi.list(beaconClient, filter);
      return res.rows;
    },
    // Background refresh every 30s — ops keeps this open while triaging.
    refetchInterval: 30_000,
  });
}

interface AddDomainVars {
  id: string;
  organizationId: string;
}
export function useResolveByAddDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, organizationId }: AddDomainVars) =>
      unknownEmailsApi.addDomain(beaconClient, id, organizationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: unknownEmailKeys.all });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (err: Error) => {
      toast.error("Couldn't add domain", { description: err.message });
    },
  });
}

interface CreateOrgVars {
  id: string;
  organizationName: string;
}
export function useResolveByCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, organizationName }: CreateOrgVars) =>
      unknownEmailsApi.createOrg(beaconClient, id, organizationName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: unknownEmailKeys.all });
      qc.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (err: Error) => {
      toast.error("Couldn't create organisation", {
        description: err.message,
      });
    },
  });
}

interface AttachTicketVars {
  id: string;
  ticketNumber: string;
}
export function useResolveByAttachTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ticketNumber }: AttachTicketVars) =>
      unknownEmailsApi.attachTicket(beaconClient, id, ticketNumber),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: unknownEmailKeys.all });
      qc.invalidateQueries({
        queryKey: ['tickets', 'detail', vars.ticketNumber],
      });
      qc.invalidateQueries({ queryKey: ['messages', vars.ticketNumber] });
    },
    onError: (err: Error) => {
      toast.error("Couldn't attach to ticket", { description: err.message });
    },
  });
}

export function useDismissUnknownEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unknownEmailsApi.dismiss(beaconClient, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: unknownEmailKeys.all });
    },
    onError: (err: Error) => {
      toast.error("Couldn't dismiss", { description: err.message });
    },
  });
}
